import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr, fileUrl } from "@/lib/api";
import { BRANDS, FUELS, MACHINE_TYPES } from "@/lib/data";
import { LogOut, Truck, Calendar, MessageSquare, Plus, Trash2, ExternalLink, X, Briefcase, FileText, GripVertical, Upload as UploadIcon, Star as StarIcon } from "lucide-react";

const BOOKING_STATUSES = ["New", "Awaiting Review", "Confirmed", "Technician Assigned", "In Progress", "Completed", "Cancelled"];
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const FORKLIFT_LIST_LIMIT = 200;

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [forklifts, setForklifts] = useState([]);
  const [careers, setCareers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [openJob, setOpenJob] = useState(null);
  const [me, setMe] = useState(null);
  const [openBooking, setOpenBooking] = useState(null);
  const [openForklift, setOpenForklift] = useState(null);

  const load = async () => {
    try {
      const [m, b, e, f, c, j] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/bookings"),
        api.get("/admin/enquiries"),
        api.get("/forklifts", { params: { limit: FORKLIFT_LIST_LIMIT } }),
        api.get("/admin/careers"),
        api.get("/admin/jobs"),
      ]);
      setMe(m.data); setBookings(b.data); setEnquiries(e.data); setForklifts(f.data); setCareers(c.data); setJobs(j.data);
    } catch (err) {
      const s = err.response?.status;
      if (s === HTTP_UNAUTHORIZED || s === HTTP_FORBIDDEN) nav("/admin/login");
      else toast.error(formatErr(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => { await api.post("/auth/logout").catch(() => {}); nav("/admin/login"); };

  const updateBooking = async (id, patch) => {
    await api.patch(`/admin/bookings/${id}`, patch); toast.success("Updated");
    setOpenBooking(null); load();
  };
  const deleteBooking = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/bookings/${id}`); load(); };
  const deleteEnquiry = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/enquiries/${id}`); load(); };
  const deleteForklift = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/forklifts/${id}`); load(); };
  const deleteCareer = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/careers/${id}`); load(); };
  const deleteJob = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/jobs/${id}`); load(); };

  const stats = {
    newBookings: bookings.filter(b => b.status === "New").length,
    confirmed: bookings.filter(b => b.status === "Confirmed").length,
    enq: enquiries.length,
    available: forklifts.filter(f => f.status === "available").length,
  };

  return (
    <div className="min-h-screen bg-[#f4f4f4]">
      <header className="bg-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/forkfix-logo.png" alt="Forkfix" className="h-9 w-auto brightness-0 invert" />
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/70">Admin</div>
          </Link>
          <div className="flex items-center gap-4">
            {me && <span className="text-xs text-white/60 hidden md:inline">{me.email}</span>}
            <button onClick={logout} className="text-xs uppercase tracking-widest font-bold flex items-center gap-2 hover:text-[#D71920]" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-display text-4xl uppercase">Dashboard</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {[["New Bookings", stats.newBookings], ["Confirmed", stats.confirmed], ["Enquiries", stats.enq], ["Available Stock", stats.available]].map(([k, v]) => (
            <div key={k} className="ff-card p-5"><div className="text-[11px] uppercase tracking-widest text-[#666]">{k}</div><div className="font-display text-4xl mt-1">{v}</div></div>
          ))}
        </div>

        <div className="mt-8 flex gap-1 border-b border-[#e5e5e5]">
          {[["bookings","Bookings",Calendar],["enquiries","Enquiries",MessageSquare],["forklifts","Forklifts",Truck],["careers","Careers",Briefcase],["jobs","Jobs",FileText]].map(([id, label, I]) => (
            <button key={id} onClick={() => setTab(id)} className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-bold border-b-2 ${tab === id ? "border-[#D71920] text-[#D71920]" : "border-transparent"}`} data-testid={`tab-${id}`}>
              <I className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full bg-white border border-[#e5e5e5] text-sm">
              <thead className="bg-[#111] text-white text-[11px] uppercase tracking-widest">
                <tr><th className="p-3 text-left">Ref</th><th className="p-3 text-left">Company</th><th className="p-3 text-left">Suburb</th><th className="p-3 text-left">Forklift</th><th className="p-3 text-left">Urgency</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.booking_id} className="border-t border-[#e5e5e5] hover:bg-[#f4f4f4]">
                    <td className="p-3 font-mono-tech font-bold">{b.booking_reference}</td>
                    <td className="p-3">{b.company_name}<div className="text-xs text-[#666]">{b.contact_name}</div></td>
                    <td className="p-3">{b.suburb}</td>
                    <td className="p-3">{b.forklift_brand} {b.forklift_model}</td>
                    <td className="p-3 text-xs">{b.urgency}</td>
                    <td className="p-3"><span className={`text-[11px] uppercase font-bold ${b.status==="New"?"text-[#D71920]":"text-[#111]"}`}>{b.status}</span></td>
                    <td className="p-3 text-right"><button onClick={() => setOpenBooking(b)} className="text-xs uppercase tracking-widest font-bold hover:text-[#D71920]" data-testid={`b-open-${b.booking_reference}`}>Open</button></td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[#666] text-sm uppercase tracking-widest">No bookings yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-6 grid gap-3">
            {enquiries.map(e => (
              <div key={e.enquiry_id} className="ff-card p-5">
                <div className="flex justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-[#D71920] font-bold">{e.kind}{e.forklift_title ? ` · ${e.forklift_title}` : ""}</div>
                    <div className="font-display text-xl mt-1">{e.name} · {e.company || "—"}</div>
                    <div className="text-sm text-[#555] mt-1">{e.phone} · {e.email}</div>
                    <div className="text-sm mt-3">{e.message}</div>
                  </div>
                  <button onClick={() => deleteEnquiry(e.enquiry_id)} className="text-[#666] hover:text-[#D71920]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {enquiries.length === 0 && <div className="ff-card p-10 text-center text-sm text-[#666] uppercase tracking-widest">No enquiries yet</div>}
          </div>
        )}

        {tab === "forklifts" && (
          <div className="mt-6">
            <button onClick={() => setOpenForklift({})} className="ff-btn-primary mb-4" data-testid="add-forklift"><Plus className="w-4 h-4" /> Add Forklift</button>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {forklifts.map(f => (
                <div key={f.forklift_id} className="ff-card">
                  <div className="aspect-video bg-[#f4f4f4]">{f.main_image && <img src={f.main_image} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="p-4">
                    <div className="text-[11px] uppercase text-[#666] tracking-widest">{f.listing_type} · {f.status}</div>
                    <div className="font-display text-lg mt-1">{f.title}</div>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => setOpenForklift(f)} className="text-xs uppercase font-bold tracking-widest hover:text-[#D71920]" data-testid={`edit-${f.slug}`}>Edit</button>
                      <Link to={`/forklifts/${f.slug}`} className="text-xs uppercase font-bold tracking-widest hover:text-[#D71920] flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View</Link>
                      <button onClick={() => deleteForklift(f.forklift_id)} className="text-xs uppercase font-bold tracking-widest text-[#666] hover:text-[#D71920] ml-auto"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "careers" && (
          <div className="mt-6 grid gap-3">
            {careers.map(c => (
              <div key={c.application_id} className="ff-card p-5">
                <div className="flex justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-widest text-[#D71920] font-bold">{c.position}</div>
                    <div className="font-display text-xl mt-1">{c.first_name} {c.last_name}</div>
                    <div className="text-sm text-[#555] mt-1">{c.phone} · {c.email}{c.location ? ` · ${c.location}` : ""}{c.years_experience ? ` · ${c.years_experience} yrs` : ""}</div>
                    <div className="text-sm mt-3 whitespace-pre-wrap">{c.description}</div>
                    {c.resume_url && (
                      <a href={fileUrl(c.resume_url)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#D71920] hover:underline" data-testid={`career-resume-${c.application_id}`}>
                        <FileText className="w-4 h-4" /> {c.resume_name || "Resume"}
                      </a>
                    )}
                  </div>
                  <button onClick={() => deleteCareer(c.application_id)} className="text-[#666] hover:text-[#D71920] self-start"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {careers.length === 0 && <div className="ff-card p-10 text-center text-sm text-[#666] uppercase tracking-widest">No applications yet</div>}
          </div>
        )}

        {tab === "jobs" && (
          <div className="mt-6">
            <button onClick={() => setOpenJob({})} className="ff-btn-primary mb-4" data-testid="add-job"><Plus className="w-4 h-4" /> Add Job Opening</button>
            <div className="grid gap-3">
              {jobs.map(j => (
                <div key={j.job_id} className="ff-card p-5">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-display text-xl uppercase">{j.title}</div>
                        {!j.published && <span className="text-[10px] uppercase tracking-widest bg-[#666] text-white px-2 py-0.5">Draft</span>}
                      </div>
                      <div className="text-xs uppercase tracking-widest text-[#666] mt-1">{j.employment_type} · {j.location}</div>
                      <p className="text-sm text-[#555] mt-2 line-clamp-2">{j.description}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setOpenJob(j)} className="text-xs uppercase font-bold tracking-widest hover:text-[#D71920]" data-testid={`edit-job-${j.job_id}`}>Edit</button>
                      <button onClick={() => deleteJob(j.job_id)} className="text-[#666] hover:text-[#D71920]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
              {jobs.length === 0 && <div className="ff-card p-10 text-center text-sm text-[#666] uppercase tracking-widest">No job openings yet — click "Add Job Opening" to publish one.</div>}
            </div>
          </div>
        )}
      </div>

      {openBooking && <BookingModal booking={openBooking} onClose={() => setOpenBooking(null)} onSave={updateBooking} onDelete={deleteBooking} />}
      {openForklift && <ForkliftModal forklift={openForklift} onClose={() => setOpenForklift(null)} onSaved={() => { setOpenForklift(null); load(); }} />}
      {openJob && <JobModal job={openJob} onClose={() => setOpenJob(null)} onSaved={() => { setOpenJob(null); load(); }} />}
    </div>
  );
}

function JobModal({ job, onClose, onSaved }) {
  const isNew = !job.job_id;
  const [f, setF] = useState({
    title: "", employment_type: "Full-time", location: "Brisbane, QLD",
    description: "", responsibilities: "", requirements: "", published: true, ...job,
  });
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (isNew) await api.post("/admin/jobs", f);
      else await api.patch(`/admin/jobs/${job.job_id}`, f);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 bg-[#111] text-white flex justify-between items-center">
          <div className="font-display text-xl uppercase">{isNew ? "Add Job Opening" : "Edit Job Opening"}</div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-3 text-sm">
          <div className="md:col-span-2"><label className="ff-label">Title</label><input value={f.title} onChange={e => upd("title", e.target.value)} className="ff-input" data-testid="job-title" /></div>
          <div><label className="ff-label">Employment Type</label>
            <select value={f.employment_type} onChange={e => upd("employment_type", e.target.value)} className="ff-input" data-testid="job-type">
              {["Full-time","Part-time","Casual","Contract","Apprenticeship"].map(x => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div><label className="ff-label">Location</label><input value={f.location} onChange={e => upd("location", e.target.value)} className="ff-input" data-testid="job-location" /></div>
          <div className="md:col-span-2"><label className="ff-label">Description</label><textarea rows={4} value={f.description} onChange={e => upd("description", e.target.value)} className="ff-input" data-testid="job-description" /></div>
          <div className="md:col-span-2"><label className="ff-label">Responsibilities</label><textarea rows={3} value={f.responsibilities} onChange={e => upd("responsibilities", e.target.value)} className="ff-input" data-testid="job-responsibilities" /></div>
          <div className="md:col-span-2"><label className="ff-label">Requirements</label><textarea rows={3} value={f.requirements} onChange={e => upd("requirements", e.target.value)} className="ff-input" data-testid="job-requirements" /></div>
          <label className="flex items-center gap-2 md:col-span-2"><input type="checkbox" checked={f.published} onChange={e => upd("published", e.target.checked)} data-testid="job-published" /> Published (visible on Careers page)</label>
        </div>
        <div className="p-6 border-t flex justify-end"><button onClick={save} className="ff-btn-primary" data-testid="save-job">Save Job</button></div>
      </div>
    </div>
  );
}

function BookingModal({ booking, onClose, onSave, onDelete }) {
  const [f, setF] = useState({ status: booking.status, internal_notes: booking.internal_notes || "", assigned_technician: booking.assigned_technician || "", confirmed_date: booking.confirmed_date || "", confirmed_time: booking.confirmed_time || "" });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 bg-[#111] text-white flex justify-between items-center">
          <div><div className="text-[11px] uppercase tracking-widest text-[#D71920]">Booking · {booking.booking_reference}</div><div className="font-display text-xl">{booking.company_name}</div></div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <div><strong>Contact:</strong> {booking.contact_name} · {booking.phone} · {booking.email}</div>
          <div><strong>Address:</strong> {booking.address}, {booking.suburb} {booking.postcode}</div>
          <div><strong>Forklift:</strong> {booking.forklift_brand} {booking.forklift_model} ({booking.fuel_type}, {booking.machine_type})</div>
          <div><strong>Service:</strong> {booking.service_type} · <strong>Urgency:</strong> {booking.urgency}</div>
          <div><strong>Problem:</strong> {booking.problem_description}</div>
          <div><strong>Preferred:</strong> {booking.preferred_date} · {booking.preferred_time}</div>
          {booking.file_urls?.length > 0 && (
            <div className="grid grid-cols-4 gap-2">{booking.file_urls.map(u => <img key={u} src={fileUrl(u)} alt="" className="aspect-square object-cover border" />)}</div>
          )}
          <div className="grid md:grid-cols-2 gap-3 pt-4 border-t">
            <div><label className="ff-label">Status</label><select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} className="ff-input">{BOOKING_STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><label className="ff-label">Assigned Technician</label><input value={f.assigned_technician} onChange={e => setF({ ...f, assigned_technician: e.target.value })} className="ff-input" /></div>
            <div><label className="ff-label">Confirmed Date</label><input type="date" value={f.confirmed_date || ""} onChange={e => setF({ ...f, confirmed_date: e.target.value })} className="ff-input" /></div>
            <div><label className="ff-label">Confirmed Time</label><input value={f.confirmed_time || ""} onChange={e => setF({ ...f, confirmed_time: e.target.value })} className="ff-input" /></div>
            <div className="md:col-span-2"><label className="ff-label">Internal Notes</label><textarea rows={3} value={f.internal_notes} onChange={e => setF({ ...f, internal_notes: e.target.value })} className="ff-input" /></div>
          </div>
          <div className="flex justify-between pt-4">
            <button onClick={() => onDelete(booking.booking_id)} className="text-xs uppercase font-bold text-[#666] hover:text-[#D71920]"><Trash2 className="w-4 h-4 inline" /> Delete</button>
            <button onClick={() => onSave(booking.booking_id, f)} className="ff-btn-primary" data-testid="save-booking">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ForkliftModal({ forklift, onClose, onSaved }) {
  const isNew = !forklift.forklift_id;
  const [f, setF] = useState({
    title: "", listing_type: "sale", status: "available", brand: "Linde", model: "", year: 2020,
    stock_number: "", hours: 0, capacity_kg: 2500, fuel_type: "LPG", machine_type: "Counterbalance",
    lift_height_mm: 4500, sale_price: 0, daily_hire_price: 0, description: "", features: "",
    main_image: "", featured: false, published: true, gallery_images: [], ...forklift,
  });
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Keep gallery as source of truth; main_image = gallery[0]
  let gallery;
  if (f.gallery_images?.length) gallery = f.gallery_images;
  else if (f.main_image) gallery = [f.main_image];
  else gallery = [];
  const setGallery = (arr) => setF(p => ({ ...p, gallery_images: arr, main_image: arr[0] || "" }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(fi => fd.append("files", fi));
      const r = await api.post("/uploads?watermark=1", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const urls = r.data?.urls || [];
      if (urls.length) setGallery([...gallery, ...urls]);
      toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? "" : "s"} · watermarked`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImg = (idx) => setGallery(gallery.filter((_, i) => i !== idx));
  const makeMain = (idx) => {
    const arr = [...gallery];
    const [x] = arr.splice(idx, 1);
    arr.unshift(x);
    setGallery(arr);
  };
  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const arr = [...gallery];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, moved);
    setGallery(arr);
    setDragIdx(null);
  };

  const save = async () => {
    try {
      const numeric = ["year", "hours", "capacity_kg", "lift_height_mm", "sale_price", "daily_hire_price", "weekly_hire_price", "monthly_hire_price"];
      const payload = { ...f, gallery_images: gallery, main_image: gallery[0] || f.main_image || "" };
      numeric.forEach(k => { if (payload[k] === "" || payload[k] === undefined) payload[k] = null; else payload[k] = Number(payload[k]); });
      if (isNew) await api.post("/admin/forklifts", payload);
      else await api.patch(`/admin/forklifts/${forklift.forklift_id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 bg-[#111] text-white flex justify-between items-center">
          <div className="font-display text-xl uppercase">{isNew ? "Add Forklift" : "Edit Forklift"}</div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 border-b">
          <div className="flex justify-between items-baseline mb-3">
            <label className="ff-label !mb-0">Photos · drag to reorder · first is the card image</label>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="text-xs uppercase tracking-widest font-bold text-[#D71920] hover:underline flex items-center gap-1"
              data-testid="upload-forklift-photos">
              <UploadIcon className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload photos"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="forklift-file-input" />
          </div>
          {gallery.length === 0 ? (
            <div className="border-2 border-dashed border-[#e5e5e5] p-6 text-center text-xs uppercase tracking-widest text-[#999]">
              No photos yet — click <strong className="text-[#D71920]">Upload photos</strong> to add JPG/PNG/WebP. A Forkfix watermark will be added automatically.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="forklift-gallery">
              {gallery.map((url, i) => (
                <div key={url + i}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(i)}
                  className={`relative aspect-[4/3] border ${i === 0 ? "border-[#D71920] border-2" : "border-[#e5e5e5]"} bg-[#f4f4f4] cursor-move group ${dragIdx === i ? "opacity-40" : ""}`}
                  data-testid={`gallery-item-${i}`}>
                  <img src={fileUrl(url)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && (
                    <div className="absolute top-1 left-1 bg-[#D71920] text-white text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 flex items-center gap-1">
                      <StarIcon className="w-2.5 h-2.5 fill-current" /> Main
                    </div>
                  )}
                  <div className="absolute top-1 right-1 bg-black/50 text-white p-1"><GripVertical className="w-3 h-3" /></div>
                  <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[10px] uppercase tracking-widest flex opacity-0 group-hover:opacity-100 transition">
                    {i !== 0 && (
                      <button type="button" onClick={() => makeMain(i)} className="flex-1 px-2 py-1 hover:bg-[#D71920]" data-testid={`make-main-${i}`}>Make main</button>
                    )}
                    <button type="button" onClick={() => removeImg(i)} className="flex-1 px-2 py-1 hover:bg-[#D71920]" data-testid={`remove-img-${i}`}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-3 text-sm">
          {[["title","Title"],["brand","Brand",true],["model","Model"],["year","Year","num"],["stock_number","Stock No."],["hours","Hours","num"],["capacity_kg","Capacity (kg)","num"],["lift_height_mm","Lift (mm)","num"],["sale_price","Sale Price","num"],["daily_hire_price","Daily Hire","num"],["weekly_hire_price","Weekly Hire","num"],["monthly_hire_price","Monthly Hire","num"]].map(([k, l, kind]) => (
            <div key={k}><label className="ff-label">{l}</label>
              {kind === true ? <select value={f[k] || ""} onChange={e => upd(k, e.target.value)} className="ff-input">{BRANDS.map(b => <option key={b}>{b}</option>)}</select>
                : <input type={kind === "num" ? "number" : "text"} value={f[k] ?? ""} onChange={e => upd(k, e.target.value)} className="ff-input" />}
            </div>
          ))}
          <div><label className="ff-label">Fuel Type</label><select value={f.fuel_type} onChange={e => upd("fuel_type", e.target.value)} className="ff-input">{FUELS.map(x => <option key={x}>{x}</option>)}</select></div>
          <div><label className="ff-label">Machine Type</label><select value={f.machine_type} onChange={e => upd("machine_type", e.target.value)} className="ff-input">{MACHINE_TYPES.map(x => <option key={x}>{x}</option>)}</select></div>
          <div><label className="ff-label">Listing Type</label><select value={f.listing_type} onChange={e => upd("listing_type", e.target.value)} className="ff-input"><option value="sale">Sale</option><option value="hire">Hire</option><option value="sale_hire">Sale + Hire</option></select></div>
          <div><label className="ff-label">Status</label><select value={f.status} onChange={e => upd("status", e.target.value)} className="ff-input"><option>available</option><option>sold</option><option>on_hire</option><option>reserved</option><option>unavailable</option></select></div>
          <div className="md:col-span-2"><label className="ff-label">Description</label><textarea rows={3} value={f.description} onChange={e => upd("description", e.target.value)} className="ff-input" /></div>
          <div className="md:col-span-2"><label className="ff-label">Features (comma separated)</label><input value={f.features} onChange={e => upd("features", e.target.value)} className="ff-input" /></div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.featured} onChange={e => upd("featured", e.target.checked)} /> Featured</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.published} onChange={e => upd("published", e.target.checked)} /> Published</label>
        </div>
        <div className="p-6 border-t flex justify-end"><button onClick={save} className="ff-btn-primary" data-testid="save-forklift">Save Forklift</button></div>
      </div>
    </div>
  );
}
